import { mapRemoteESGIndicatorToModel } from "src/app/core/valuation-process/_models/esg-indicator.model";
import { ValuationProcessApartmentModel, ValuationProcessAssetClassGeneralInfoModel, ValuationProcessHouseModel, ValuationProcessLandModel, ValuationProcessOfficeModel, ValuationProcessParkingModel, ValuationProcessRetailBuildingModel, ValuationProcessRetailShopModel, ValuationProcessSpecialPurposeModel, ValuationProcessTargetPropertyInfo, ValuationProcessTargetPropertyModel, ValuationProcessTargetPropertyRemote, ValuationProcessWareouseModel } from "../../valuation-process/types/asset-class/target-property"
import { getCenter } from "../../valuation-process/utils";
import { mapApartmentInternalAspectRemoteToDomain } from "../../valuation-process/types/asset-class/utils/internal-aspect";
import { mapVApartmentGroundRemoteToDomain } from "../../valuation-process/types/asset-class/utils/ground";
import { mapVAssetClassSizeRemoteToDomain } from "../../valuation-process/types/asset-class/utils/size";
import { mapVTPHouseRemoteToDomain, mapVTPLandRemoteToDomain, mapVTPOfficeRemoteToDomain, mapVTPParkingRemoteToDomain, mapVTPRetailBuildingRemoteToDomain, mapVTPRetailShopRemoteToDomain, mapVTPSpecialPurposeRemoteToDomain, mapVTPWarehouseRemoteToDomain } from "../../valuation-process/types/asset-class/utils/target-property";
import { ValuationComparableMapData } from "src/app/core/valuation-process/_models";
import { VApartmentComparableRemote } from "../../valuation-process/types/asset-class/api/comparable-apartment";
import { VCHouseRemote } from "../../valuation-process/types/asset-class/api/comparable-house";
import { VCOfficeRemote } from "../../valuation-process/types/asset-class/api/comparable-office";
import { VCRetailShopRemote } from "../../valuation-process/types/asset-class/api/comparable-retail-shop";
import { VCRetailBuildingRemote } from "../../valuation-process/types/asset-class/api/comparable-retail-building";
import { VCWarehouseRemote } from "../../valuation-process/types/asset-class/api/comparable-warehouse";
import { VCParkingRemote } from "../../valuation-process/types/asset-class/api/comparable-parking";
import { VCLandRemote } from "../../valuation-process/types/asset-class/api/comparable-land";
import { VComparable } from "../../valuation-process/types/asset-class/comparable";
import { mapConsiderationRemoteToDomain } from "../../valuation-process/types/asset-class/utils/consideration";
import { VCApartmentModel } from "../../valuation-process/types/asset-class/apartment";
import { displayConsiderations } from "../../valuation-process/types/asset-class/common";
import { mapComparableOfficeRemoteToDomain, mapVCHouseRemoteToDomain, mapVCLandRemoteToDomain, mapVCParkingRemoteToDomain, mapVCRetailBuildingRemoteToDomain, mapVCRetailShopRemoteToDomain, mapVCSpecialPurposeRemoteToDomain, mapVCWarehouseRemoteToDomain } from "../../valuation-process/types/asset-class/utils/comparable";
import { VCSpecialPurposeRemote } from "../../valuation-process/types/asset-class/api/comparable-special-purpose";

// top_property_type_id of Land, shared by every land sub type (Brownfield, Greenfield, ...)
const LAND_PROPERTY_TYPE_ID = 3

// Comparable
export type ValuationComparableRemoteData = {
  id: number,
  refNum: string,
  propertySubTypeId: 1 | 3 | 17 | 5 | 11 | 7 | 2 | 18,
  latitude: string,
  longitude: string
} | {
  id: number,
  refNum: string,
  propertySubTypeId: 13,
  land_paths: any[]
  latitude: string,
  longitude: string
}

export type ValuationComparableFullRemoteData
  = VApartmentComparableRemote
  | VCHouseRemote
  | VCOfficeRemote
  | VCRetailShopRemote
  | VCRetailBuildingRemote
  | VCWarehouseRemote
  | VCParkingRemote
  | VCLandRemote
  | VCSpecialPurposeRemote


export function mapFullRemoteToDomain(comparable: ValuationComparableFullRemoteData): VComparable | null {
  switch (comparable.property_sub_type_id) {
    case 1:
      const considerations = mapConsiderationRemoteToDomain(comparable.considerations)
      const data: VCApartmentModel = {
        id: comparable.id,
        refNum: comparable.refNum,
        propertySubType: 'Apartment',
        propertySubTypeName: comparable.propertySubTypeName,
        propertyTypeName: comparable.topPropertyTypeName,
        propertyInfo: {
          bedrooms: comparable.propertyInfo.bedrooms,
          bathrooms: comparable.propertyInfo.bathrooms
        },
        locationData: {
          latitude: Number(comparable.locationData.latitude),
          longitude: Number(comparable.locationData.longitude),
          country: comparable.locationData.countryName,
          city: comparable.locationData.cityName,
          zipCode: comparable.locationData.zipCode,
          address: comparable.locationData.address,
          locationGrade: comparable.locationData.locationGradeName,
          locationSurrounding: null,
          timezoneOffset: comparable.locationData.timezoneOffset
        },
        internalAspect: mapApartmentInternalAspectRemoteToDomain(comparable.internalAspect),
        property: comparable.property,
        building: comparable.buildingInfo,
        sizes: comparable.sizes.map(size => ({
          standardMeasurementId: size.standardMeasurementId,
          standardMeasurementName: size.standard_measurement_name,
          unitOfAreaMeasurementId: size.unitOfAreaMeasurementId,
          unitOfAreaMeasurementAcronym: size.unit_of_area_measurement_acronym,
          size: size.size
        })),
        considerations,
        displayConsiderations: displayConsiderations(considerations),
        pictures: comparable.pictures,
        status: comparable.status,
        usedTimes: comparable.usedTimes,
        oldBuildingInfo: comparable.oldBuildingInfo,
        ground: mapVApartmentGroundRemoteToDomain(comparable.ground),
        esgIndicator: mapRemoteESGIndicatorToModel(comparable.esgIndicator)
      }
      return data
    case 3: {
      return mapComparableOfficeRemoteToDomain(comparable)
    }
    case 17: {
      return mapVCHouseRemoteToDomain(comparable)
    }
    case 5: {
      return mapVCRetailShopRemoteToDomain(comparable)
    }
    case 11: {
      return mapVCRetailBuildingRemoteToDomain(comparable)
    }
    case 7: {
      return mapVCWarehouseRemoteToDomain(comparable)
    }
    case 2: {
      return mapVCParkingRemoteToDomain(comparable)
    }
    case 13: {
      return mapVCLandRemoteToDomain(comparable)
    }
    case 18: {
      return mapVCSpecialPurposeRemoteToDomain(comparable)
    }
    default:
      return null
  }
}

export function mapRemoteToDomain(comparable: ValuationComparableRemoteData): ValuationComparableMapData | null {
  switch (comparable.propertySubTypeId) {
    case 1:
      return {
        id: comparable.id,
        refNum: comparable.refNum,
        propertySubType: 'Apartment',
        latitude: Number(comparable.latitude),
        longitude: Number(comparable.longitude)
      }
    case 17: {
      return {
        id: comparable.id,
        refNum: comparable.refNum,
        propertySubType: 'House',
        latitude: Number(comparable.latitude),
        longitude: Number(comparable.longitude)
      }
    }
    case 3: {
      return {
        id: comparable.id,
        refNum: comparable.refNum,
        propertySubType: 'Office',
        latitude: Number(comparable.latitude),
        longitude: Number(comparable.longitude)
      }
    }
    case 5: {
      return {
        id: comparable.id,
        refNum: comparable.refNum,
        propertySubType: 'Retail Shop',
        latitude: Number(comparable.latitude),
        longitude: Number(comparable.longitude)
      }
    }
    case 11: {
      return {
        id: comparable.id,
        refNum: comparable.refNum,
        propertySubType: 'Retail Building',
        latitude: Number(comparable.latitude),
        longitude: Number(comparable.longitude)
      }
    }
    case 7: {
      return {
        id: comparable.id,
        refNum: comparable.refNum,
        propertySubType: 'Warehouse',
        latitude: Number(comparable.latitude),
        longitude: Number(comparable.longitude)
      }
    }
    case 2: {
      return {
        id: comparable.id,
        refNum: comparable.refNum,
        propertySubType: 'Parking',
        latitude: Number(comparable.latitude),
        longitude: Number(comparable.longitude)
      }
    }
    case 13: {
      return {
        id: comparable.id,
        refNum: comparable.refNum,
        propertySubType: 'Land',
        latitude: Number(comparable.latitude),
        longitude: Number(comparable.longitude)
      }
    }
    case 18: {
      return {
        id: comparable.id,
        refNum: comparable.refNum,
        propertySubType: 'Special Purpose',
        latitude: Number(comparable.latitude),
        longitude: Number(comparable.longitude)
      }
    }
    default:
      return null
  }
}


// Target Property
export function mapTPRemoteToTpModel(tpRemote: ValuationProcessTargetPropertyRemote): ValuationProcessTargetPropertyModel {
  const generalInfo = mapTPRemoteToGeneralInfo(tpRemote)
  return {
    id: tpRemote.id,
    name: tpRemote.name,

    generalInfo: generalInfo,
    info: mapFullInfoRemoteToDomain(tpRemote, generalInfo),

    instructedToMeasure: tpRemote.instructedToMeasure,
    inspectionDate: tpRemote.inspectionDate,
    inspectionType: tpRemote.inspectionType,
    draftDate: tpRemote.draftDate,
    finalDate: tpRemote.finalDate,
    timezone: tpRemote.locationData.timezoneOffset,

    pictures: tpRemote.pictures,
    buildingInfo: tpRemote.buildingInfo,
    countryCurrency: tpRemote.country_currency,
    reportingCurrency: tpRemote.reporting_currency
  }
}

function mapTPRemoteToGeneralInfo(tpRemote: ValuationProcessTargetPropertyRemote): ValuationProcessAssetClassGeneralInfoModel {
  switch (tpRemote.propertySubTypeId) {
    case 1: {
      const data: ValuationProcessApartmentModel = {
        propertyType: 'Residential',
        propertyTypeName: tpRemote.propertyTypeName,
        propertySubType: 'Apartment',
        propertySubTypeId: 1,
        propertySubTypeName: tpRemote.propertySubTypeName,
        subTypeCategoryId: tpRemote.subTypeCategoryId,
        subTypeCategoryName: tpRemote.subTypeCategoryName,
        subCategoryId: tpRemote.subCategoryId,
        subCategoryName: tpRemote.subCategoryName,
        locationData: {
          countryName: tpRemote.locationData?.countryName,
          cityName: tpRemote.locationData?.cityName,
          zipCode: tpRemote.locationData?.zipCode,
          address: tpRemote.locationData?.address,
          latitude: Number(tpRemote.locationData?.latitude),
          longitude: Number(tpRemote.locationData?.longitude),
          timezoneOffset: tpRemote.locationData?.timezoneOffset
        },
        locationGrade: tpRemote.locationGrade,
        measurementStandardId: tpRemote.measurementStandardId,
        measurementStandard: tpRemote.measurementStandardName,
        measurementStandardModel: tpRemote.measurementStandard,
        size: tpRemote.size.value,
        unitAreaMeasurementAcronym: tpRemote.size.unitAreaMeasurementAcronym,
        unitAreaMeasurementModel: tpRemote.size.unitAreaMeasurement,
        floorNumberingSchemeId: tpRemote.floorNumberingSchemeId,
        accommodation: tpRemote.accommodation,
        bedroom: tpRemote.bedroom,
        selectedExternalArea: tpRemote.selectedExternalArea,
        unitMeasurementName: tpRemote.size.unitMeasurementName,
        esgIndicator: mapRemoteESGIndicatorToModel(tpRemote.esgIndicator)
      }
      return data;
    }
    case 3: {
      const data: ValuationProcessOfficeModel = {
        propertyType: 'Commercial',
        propertyTypeName: tpRemote.propertyTypeName,
        propertySubType: 'Office',
        propertySubTypeId: 3,
        propertySubTypeName: tpRemote.propertySubTypeName,
        subTypeCategoryId: tpRemote.subTypeCategoryId,
        subTypeCategoryName: tpRemote.subTypeCategoryName,
        subCategoryId: tpRemote.subCategoryId,
        subCategoryName: tpRemote.subCategoryName,
        locationData: {
          countryName: tpRemote.locationData?.countryName,
          cityName: tpRemote.locationData?.cityName,
          zipCode: tpRemote.locationData?.zipCode,
          address: tpRemote.locationData?.address,
          latitude: Number(tpRemote.locationData?.latitude),
          longitude: Number(tpRemote.locationData?.longitude),
          timezoneOffset: tpRemote.locationData?.timezoneOffset
        },
        locationGrade: tpRemote.locationGrade,
        measurementStandardId: tpRemote.measurementStandardId,
        measurementStandard: tpRemote.measurementStandardName,
        measurementStandardModel: tpRemote.measurementStandard,
        size: tpRemote.size.value,
        unitAreaMeasurementAcronym: tpRemote.size.unitAreaMeasurementAcronym,
        unitAreaMeasurementModel: tpRemote.size.unitAreaMeasurement,
        floorNumberingSchemeId: tpRemote.floorNumberingSchemeId,
        accommodation: tpRemote.accommodation,
        selectedExternalArea: tpRemote.selectedExternalArea,
        unitMeasurementName: tpRemote.size.unitMeasurementName,
        esgIndicator: mapRemoteESGIndicatorToModel(tpRemote.esgIndicator)
      }
      return data;
    }
    case 5: {
      const data: ValuationProcessRetailShopModel = {
        propertyType: 'Commercial',
        propertyTypeName: tpRemote.propertyTypeName,
        propertySubType: 'Retail Shop',
        propertySubTypeId: 5,
        propertySubTypeName: tpRemote.propertySubTypeName,
        subTypeCategoryId: tpRemote.subTypeCategoryId,
        subTypeCategoryName: tpRemote.subTypeCategoryName,
        subCategoryId: tpRemote.subCategoryId,
        subCategoryName: tpRemote.subCategoryName,
        locationData: {
          countryName: tpRemote.locationData?.countryName,
          cityName: tpRemote.locationData?.cityName,
          zipCode: tpRemote.locationData?.zipCode,
          address: tpRemote.locationData?.address,
          latitude: Number(tpRemote.locationData?.latitude),
          longitude: Number(tpRemote.locationData?.longitude),
          timezoneOffset: tpRemote.locationData?.timezoneOffset
        },
        locationGrade: tpRemote.locationGrade,
        measurementStandardId: tpRemote.measurementStandardId,
        measurementStandard: tpRemote.measurementStandardName,
        measurementStandardModel: tpRemote.measurementStandard,
        size: tpRemote.size.value,
        unitAreaMeasurementAcronym: tpRemote.size.unitAreaMeasurementAcronym,
        unitAreaMeasurementModel: tpRemote.size.unitAreaMeasurement,
        floorNumberingSchemeId: tpRemote.floorNumberingSchemeId,
        accommodation: tpRemote.accommodation,
        selectedExternalArea: tpRemote.selectedExternalArea,
        unitMeasurementName: tpRemote.size.unitMeasurementName,
        esgIndicator: mapRemoteESGIndicatorToModel(tpRemote.esgIndicator)
      }
      return data;
    }
    case 11: {
      const data: ValuationProcessRetailBuildingModel = {
        propertyType: 'Commercial',
        propertyTypeName: tpRemote.propertyTypeName,
        propertySubType: 'Retail Building',
        propertySubTypeId: 11,
        propertySubTypeName: tpRemote.propertySubTypeName,
        subTypeCategoryId: tpRemote.subTypeCategoryId,
        subTypeCategoryName: tpRemote.subTypeCategoryName,
        subCategoryId: tpRemote.subCategoryId,
        subCategoryName: tpRemote.subCategoryName,
        locationData: {
          countryName: tpRemote.locationData?.countryName,
          cityName: tpRemote.locationData?.cityName,
          zipCode: tpRemote.locationData?.zipCode,
          address: tpRemote.locationData?.address,
          latitude: Number(tpRemote.locationData?.latitude),
          longitude: Number(tpRemote.locationData?.longitude),
          timezoneOffset: tpRemote.locationData?.timezoneOffset
        },
        locationGrade: tpRemote.locationGrade,
        measurementStandardId: tpRemote.measurementStandardId,
        measurementStandard: tpRemote.measurementStandardName,
        measurementStandardModel: tpRemote.measurementStandard,
        size: tpRemote.size.value,
        unitAreaMeasurementAcronym: tpRemote.size.unitAreaMeasurementAcronym,
        unitAreaMeasurementModel: tpRemote.size.unitAreaMeasurement,
        floorNumberingSchemeId: tpRemote.floorNumberingSchemeId,
        accommodation: tpRemote.accommodation,
        propertyGrade: tpRemote.propertyGrade,
        unitMeasurementName: tpRemote.size.unitMeasurementName,
        esgIndicator: mapRemoteESGIndicatorToModel(tpRemote.esgIndicator)
      }
      return data;
    }
    case 17: {
      const data: ValuationProcessHouseModel = {
        propertyType: 'Residential',
        propertyTypeName: tpRemote.propertyTypeName,
        propertySubType: 'House',
        propertySubTypeId: 17,
        propertySubTypeName: tpRemote.propertySubTypeName,
        subTypeCategoryId: tpRemote.subTypeCategoryId,
        subTypeCategoryName: tpRemote.subTypeCategoryName,
        subCategoryId: tpRemote.subCategoryId,
        subCategoryName: tpRemote.subCategoryName,
        locationData: {
          countryName: tpRemote.locationData?.countryName,
          cityName: tpRemote.locationData?.cityName,
          zipCode: tpRemote.locationData?.zipCode,
          address: tpRemote.locationData?.address,
          latitude: Number(tpRemote.locationData?.latitude),
          longitude: Number(tpRemote.locationData?.longitude),
          timezoneOffset: tpRemote.locationData?.timezoneOffset
        },
        locationGrade: tpRemote.locationGrade,
        measurementStandardId: tpRemote.measurementStandardId,
        measurementStandard: tpRemote.measurementStandardName,
        measurementStandardModel: tpRemote.measurementStandard,
        size: tpRemote.size.value,
        unitAreaMeasurementAcronym: tpRemote.size.unitAreaMeasurementAcronym,
        unitAreaMeasurementModel: tpRemote.size.unitAreaMeasurement,
        floorNumberingSchemeId: tpRemote.floorNumberingSchemeId,
        accommodation: tpRemote.accommodation,
        propertyGrade: tpRemote.propertyGrade,
        unitMeasurementName: tpRemote.size.unitMeasurementName,
        esgIndicator: mapRemoteESGIndicatorToModel(tpRemote.esgIndicator)
      }
      return data;
    }
    case 2: {
      const data: ValuationProcessParkingModel = {
        propertyType: 'Commercial',
        propertyTypeName: tpRemote.propertyTypeName,
        propertySubType: 'Parking',
        propertySubTypeId: 2,
        propertySubTypeName: tpRemote.propertySubTypeName,
        subTypeCategoryId: tpRemote.subTypeCategoryId,
        subTypeCategoryName: tpRemote.subTypeCategoryName,
        subCategoryId: tpRemote.subCategoryId,
        subCategoryName: tpRemote.subCategoryName,
        locationData: {
          countryName: tpRemote.locationData?.countryName,
          cityName: tpRemote.locationData?.cityName,
          zipCode: tpRemote.locationData?.zipCode,
          address: tpRemote.locationData?.address,
          latitude: Number(tpRemote.locationData?.latitude),
          longitude: Number(tpRemote.locationData?.longitude),
          timezoneOffset: tpRemote.locationData?.timezoneOffset
        },
        locationGrade: tpRemote.locationGrade,
        measurementStandardId: tpRemote.measurementStandardId,
        measurementStandard: tpRemote.measurementStandardName,
        measurementStandardModel: tpRemote.measurementStandard,
        size: tpRemote.size.value,
        unitAreaMeasurementAcronym: tpRemote.size.unitAreaMeasurementAcronym,
        unitAreaMeasurementModel: tpRemote.size.unitAreaMeasurement,
        floorNumberingSchemeId: tpRemote.floorNumberingSchemeId,
        accommodation: tpRemote.accommodation,
        unitMeasurementName: tpRemote.size.unitMeasurementName,
        esgIndicator: mapRemoteESGIndicatorToModel(tpRemote.esgIndicator)
      }
      return data;
    }
    case 7: {
      const data: ValuationProcessWareouseModel = {
        propertyType: 'Industrial',
        propertyTypeName: tpRemote.propertyTypeName,
        propertySubType: 'Warehouse',
        propertySubTypeId: 7,
        propertySubTypeName: tpRemote.propertySubTypeName,
        subTypeCategoryId: tpRemote.subTypeCategoryId,
        subTypeCategoryName: tpRemote.subTypeCategoryName,
        subCategoryId: tpRemote.subCategoryId,
        subCategoryName: tpRemote.subCategoryName,
        locationData: {
          countryName: tpRemote.locationData?.countryName,
          cityName: tpRemote.locationData?.cityName,
          zipCode: tpRemote.locationData?.zipCode,
          address: tpRemote.locationData?.address,
          latitude: Number(tpRemote.locationData?.latitude),
          longitude: Number(tpRemote.locationData?.longitude),
          timezoneOffset: tpRemote.locationData?.timezoneOffset
        },
        locationGrade: tpRemote.locationGrade,
        measurementStandardId: tpRemote.measurementStandardId,
        measurementStandard: tpRemote.measurementStandardName,
        measurementStandardModel: tpRemote.measurementStandard,
        size: tpRemote.size.value,
        unitAreaMeasurementAcronym: tpRemote.size.unitAreaMeasurementAcronym,
        unitAreaMeasurementModel: tpRemote.size.unitAreaMeasurement,
        floorNumberingSchemeId: tpRemote.floorNumberingSchemeId,
        accommodation: tpRemote.accommodation,
        propertyGrade: tpRemote.propertyGrade,
        unitMeasurementName: tpRemote.size.unitMeasurementName,
        esgIndicator: mapRemoteESGIndicatorToModel(tpRemote.esgIndicator)
      }
      return data;
    }
    // Brownfield (13) and Greenfield (16) are both land sub types
    case 13:
    case 16: {
      return mapTPRemoteToLandGeneralInfo(tpRemote)
    }
    case 18: {
      const data: ValuationProcessSpecialPurposeModel = {
        propertySubType: 'Special Purpose',
        propertySubTypeId: 18,
        locationData: {
          countryName: tpRemote.locationData?.countryName,
          cityName: tpRemote.locationData?.cityName,
          zipCode: tpRemote.locationData?.zipCode,
          address: tpRemote.locationData?.address,
          latitude: Number(tpRemote.locationData?.latitude),
          longitude: Number(tpRemote.locationData?.longitude),
          timezoneOffset: tpRemote.locationData?.timezoneOffset
        },
        locationGrade: tpRemote.locationGrade,
        measurementStandardId: tpRemote.measurementStandardId,
        measurementStandard: tpRemote.measurementStandardName,
        measurementStandardModel: tpRemote.measurementStandard,
        size: tpRemote.size.value,
        unitAreaMeasurementAcronym: tpRemote.size.unitAreaMeasurementAcronym,
        unitAreaMeasurementModel: tpRemote.size.unitAreaMeasurement,
        floorNumberingSchemeId: tpRemote.floorNumberingSchemeId,
        accommodation: tpRemote.accommodation,
        propertyGrade: tpRemote.propertyGrade,
        unitMeasurementName: tpRemote.size.unitMeasurementName,
        esgIndicator: mapRemoteESGIndicatorToModel(tpRemote.esgIndicator),
        specialPurposeType: tpRemote.aboutProperty.specialPurposeType
      }
      return data;
    }
    default:
      // tenants can define their own sub types, so fall back on the top
      // property type (3 = Land) instead of returning null
      return tpRemote.propertyTypeId == LAND_PROPERTY_TYPE_ID
        ? mapTPRemoteToLandGeneralInfo(tpRemote)
        : null
  }
}

function mapTPRemoteToLandGeneralInfo(tpRemote: ValuationProcessTargetPropertyRemote): ValuationProcessLandModel {
  const center = getCenter(tpRemote.landParcel?.lp_path ?? [])
  const landAreas = tpRemote.landParcel?.landAreas ?? []
  const totalLandParcel = landAreas.length
  let totalLandArea = 0
  let unitMeasurement = ''
  let unitMeasurementName = ''
  if (totalLandParcel > 0) {
    unitMeasurement = landAreas[0].unit_area_measurement_acronym
    unitMeasurementName = landAreas[0].unit_area_measurement_name
    totalLandArea = landAreas.reduce((acc, v) => acc + v.surface, 0)
  }
  const data: ValuationProcessLandModel = {
    propertySubType: 'Land',
    propertySubTypeId: 13,
    propertyTypeName: tpRemote.propertyTypeName,
    propertySubTypeName: tpRemote.propertySubTypeName,
    locationData: {
      countryName: tpRemote.locationData?.countryName,
      cityName: tpRemote.locationData?.cityName,
      zipCode: tpRemote.locationData?.zipCode,
      address: tpRemote.locationData?.address,
      latitude: center.lat,
      longitude: center.lng,
      timezoneOffset: tpRemote.locationData?.timezoneOffset
    },
    locationGrade: tpRemote.locationGrade,
    planningStatusName: tpRemote.landParcel?.planningStatusName,
    coordinateReferenceSystemName: tpRemote.landParcel?.coordinateRefSystem,
    numOfBuilding: tpRemote.landParcel?.building_exists == 0 ? tpRemote.landParcel.totalBuildings : 'Unknown',
    totalLandArea,
    totalLandParcel,
    unitMeasurement,
    unitMeasurementName,
  }
  return data;
}

function mapFullInfoRemoteToDomain(tpRemote: ValuationProcessTargetPropertyRemote, generalInfo: ValuationProcessAssetClassGeneralInfoModel): ValuationProcessTargetPropertyInfo {
  if (!generalInfo) {
    return null
  }
  switch (generalInfo.propertySubType) {
    case 'Apartment': {
      return {
        refNum: 'tp',
        propertySubType: generalInfo.propertySubType,
        propertySubTypeName: tpRemote.propertySubTypeName,
        building: {
          name: tpRemote.buildingInfo.name,
          completionYear: tpRemote.buildingInfo.completionYear,
          buildingType: tpRemote.buildingInfo.buildingType == 'N/A' || tpRemote.buildingInfo.buildingType == null ? 'Unknown' : tpRemote.buildingInfo.buildingType,
          buildingTypeComment: tpRemote.buildingInfo.buildingTypeComment,
          buildingGrade: tpRemote.buildingInfo.buildingGrade == 'N/A' || tpRemote.buildingInfo.buildingGrade == null ? 'Unknown' : tpRemote.buildingInfo.buildingGrade,
          energyEfficiencyGrade: tpRemote.buildingInfo.energyEfficiencyGrade,
          developer: tpRemote.buildingInfo.developer,
          anchorTenant: tpRemote.buildingInfo.anchorTenant,
          foundationType: tpRemote.buildingInfo.foundationType,
          buildingDescription: tpRemote.buildingInfo.buildingDescription,
          pictures: tpRemote.buildingInfo.pictures
        },
        locationData: {
          latitude: generalInfo.locationData.latitude,
          longitude: generalInfo.locationData.longitude,
          country: generalInfo.locationData.countryName,
          city: generalInfo.locationData.cityName,
          zipCode: generalInfo.locationData.zipCode,
          address: generalInfo.locationData.address,
          timezoneOffset: generalInfo.locationData.timezoneOffset,
          locationGrade: generalInfo.locationGrade == 'N/A' ? 'Unknown' : generalInfo.locationGrade,
          locationSurrounding: tpRemote.locationSurrounding
        },
        locationSurrounding: tpRemote.locationSurrounding,
        inspectionInfo: {
          typeOfInspection: tpRemote.inspectionTypeId,
          inspectionDate: new Date(tpRemote.inspectionDate),
          anyLimitationOrRestriction: tpRemote.anyLimitationsOrRestrictions,
          limitationDescription: tpRemote.limitationDesc
        },
        property: {
          subCategory: tpRemote.subCategoryName == 'N/A' ? 'Unknown' : tpRemote.subCategoryName,
          subTypeCategory: tpRemote.subTypeCategoryName == 'N/A' ? 'Unknown' : tpRemote.subTypeCategoryName,
          generalDescription: tpRemote.aboutProperty.generalDescription,
          stateOfRepair: tpRemote.aboutProperty.stateOfRepairName,
          handoverStandard: tpRemote.aboutProperty.handoverStandardName,
          floors: tpRemote.aboutProperty.floorLocation,
          energyEfficiencyGrade: tpRemote.aboutProperty.energyEfficiencyGradeName,
        },
        pictures: tpRemote.pictures,
        oldBuildingInfo: tpRemote.oldBuildingInfo,
        internalAspect: mapApartmentInternalAspectRemoteToDomain(tpRemote.internalAspect),
        ground: mapVApartmentGroundRemoteToDomain(tpRemote.ground),
        sizes: mapVAssetClassSizeRemoteToDomain(tpRemote.sizes),
        esgIndicator: mapRemoteESGIndicatorToModel(tpRemote.esgIndicator)
      }
    }
    case 'House': {
      return mapVTPHouseRemoteToDomain(tpRemote, generalInfo)
    }
    case 'Office': {
      return mapVTPOfficeRemoteToDomain(tpRemote, generalInfo)
    }
    case 'Retail Shop': {
      return mapVTPRetailShopRemoteToDomain(tpRemote, generalInfo)
    }
    case 'Retail Building': {
      return mapVTPRetailBuildingRemoteToDomain(tpRemote, generalInfo)
    }
    case 'Warehouse': {
      return mapVTPWarehouseRemoteToDomain(tpRemote, generalInfo)
    }
    case 'Parking': {
      return mapVTPParkingRemoteToDomain(tpRemote, generalInfo)
    }
    case 'Land': {
      return mapVTPLandRemoteToDomain(tpRemote, generalInfo)
    }
    case 'Special Purpose': {
      return mapVTPSpecialPurposeRemoteToDomain(tpRemote, generalInfo)
    }
    default: {
      return null
    }
  }
}